import React, { useRef, useEffect, useState } from 'react';
import { Row, Column } from '@xingternal/xing-grid';
import { IntlShape, useIntl } from 'react-intl';

import slugify from '@sindresorhus/slugify';

// We need to have a global css so postcss doesn't scramble with the class names
// that are being rendered dangerously by react
import './PageContentGlobal.css?global';
import styles from './PageContent.css';

import { TableOfContents } from './TableOfContents';

import { PageDetails } from '../../models/page';

const formatDate = (date: string, language: string): string => {
  return new Date(date).toLocaleDateString(language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const appendLiveDateUnderTitle = (
  pageHtml: HTMLDivElement,
  pageDetails: PageDetails,
  formatMessage: IntlShape['formatMessage'],
  language: string
): void => {
  const titles = pageHtml.querySelectorAll('h1');

  if (titles.length !== 1) {
    return;
  }

  const titleNode = titles[0];
  titleNode.className = 'title';

  const liveDate = formatDate(pageDetails.liveDate, language);
  const paragraph = document.createElement('p');
  paragraph.className = 'live-date';

  const text = formatMessage({ id: 'LEGAL_PAGES_STAND' });
  paragraph.innerHTML = `${text} ${liveDate}`;

  titleNode.parentNode.insertBefore(paragraph, titleNode.nextSibling);
};

const positionViewPort = (): void => {
  if (window.location.hash) {
    try {
      return document.querySelector(`${window.location.hash}`).scrollIntoView();
    } catch (e) {
      return window.scrollTo(0, 0);
    }
  }
  return window.scrollTo(0, 0);
};

const updateHeadingIds = (pageHtml: HTMLDivElement): Record<string, number> =>
  [...pageHtml.querySelectorAll<HTMLHeadingElement>('h2, h3')].reduce(
    (headingMap, heading) => {
      const id =
        heading.id || slugify(heading.textContent.replace(/^[0-9]+/, ''));

      headingMap[id] = !isNaN(headingMap[id]) ? ++headingMap[id] : 0;
      if (headingMap[id]) {
        heading.id = id + '-' + headingMap[id];
      } else {
        heading.id = id;
      }
      return headingMap;
    },
    {}
  );

const NestedCounterWrapper = ({
  children,
  nestedCounterList,
}: {
  nestedCounterList: boolean;
  children: React.ReactElement;
}): React.ReactElement => {
  if (!nestedCounterList) {
    return children;
  }

  return <div className={'nested-counter'}>{children}</div>;
};

export const PageContent = ({
  pageDetails,
  showLiveDate = true,
}: {
  pageDetails: PageDetails;
  showLiveDate?: boolean;
}): React.ReactElement => {
  const { formatMessage } = useIntl();
  const [htmlReady, setHtmlReady] = useState(false);
  const [language, setLanguage] = useState('');

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current || !language) {
      return;
    }

    const divElement = contentRef.current;
    updateHeadingIds(divElement);
    positionViewPort();
    if (showLiveDate) {
      appendLiveDateUnderTitle(
        divElement,
        pageDetails,
        formatMessage,
        language
      );
    }

    setHtmlReady(true);
  }, [contentRef, language]);

  useEffect(() => {
    // The language which you can adjust in the settings, is available in the
    // language cookie. This function gets the cookie value of a given name
    const getCookie = (name: string) => {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith(name + '=')) {
          return decodeURIComponent(cookie.substring(name.length + 1));
        }
      }
      return '';
    };

    const languageCookie = getCookie('language');

    setLanguage(languageCookie || 'de');
  }, []);

  return (
    <Row className={styles.pageContainer}>
      <Column size={12} sizeTablet={pageDetails.hideToc ? 12 : 8}>
        <NestedCounterWrapper nestedCounterList={pageDetails.nestedCounterList}>
          <div
            ref={contentRef}
            className="content-page"
            dangerouslySetInnerHTML={{ __html: pageDetails.content }}
          />
        </NestedCounterWrapper>
      </Column>

      {!pageDetails.hideToc && (
        <Column className="noprint" size={0} sizeTablet={4}>
          {htmlReady && <TableOfContents />}
        </Column>
      )}
    </Row>
  );
};
